/**
 * PageNotificationPermission
 * Shared page component for requesting notification permissions across games.
 * Uses Capacitor's LocalNotifications API for cross-platform support.
 */

import React, { useState } from 'react';
import { BasePageProps } from '../../Page/PageTypes';
import { ButtonStylized, ButtonStylizedType } from '../../ButtonStylized/ButtonStylized';
import { requestNotificationPermission, NotificationPermission } from '@mental-mint/shared-utils';
import { Bell, BellOff } from 'lucide-react';
import { motion } from 'framer-motion';
import './PageNotificationPermission.css';

/**
 * Props for the PageNotificationPermission component
 */
export interface PageNotificationPermissionProps extends BasePageProps {
  /** Custom message to display (default: generic notification message) */
  message?: string;
  /** Callback when permission is granted or denied */
  onPermissionResponse?: (granted: boolean) => void;
}

/**
 * Page component for requesting notification permissions
 */
export const PageNotificationPermission: React.FC<PageNotificationPermissionProps> = ({
  message = 'Stay updated with notifications',
  onRequestClose,
  onPermissionResponse
}) => {
  const [isRequesting, setIsRequesting] = useState(false);
  const [isDenied, setIsDenied] = useState(false);

  const handleEnableNotifications = async () => {
    setIsRequesting(true);
    try {
      const result = await requestNotificationPermission();
      const granted = result === NotificationPermission.GRANTED;
      
      if (result === NotificationPermission.DENIED) {
        setIsDenied(true);
        setIsRequesting(false);
        return;
      }
      
      if (onPermissionResponse) {
        onPermissionResponse(granted);
      }
      
      // Close modal after permission request
      if (onRequestClose) {
        onRequestClose();
      }
    } catch (error) {
      console.error('[PageNotificationPermission] Error requesting permission:', error);
      setIsRequesting(false);
    }
  };

  const handleDecline = () => {
    if (onPermissionResponse) {
      onPermissionResponse(false);
    }
    
    if (onRequestClose) {
      onRequestClose();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="page-notification-permission"
    >
      <div className="notification-permission-content">
        {/* Icon Container with animation */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className={`notification-icon-container${isDenied ? ' denied' : ''}`}
        >
          {isDenied ? (
            <BellOff className="notification-icon denied" size={64} strokeWidth={2.5} />
          ) : (
            <Bell className="notification-icon" size={64} strokeWidth={2.5} />
          )}
        </motion.div>
        
        <p className="notification-message">
          {isDenied 
            ? 'Failed to enable notifications. You can enable them later in your device settings if you change your mind.'
            : message
          }
        </p>
        
        <div className="notification-buttons">
          {isDenied ? (
            <ButtonStylized
              buttonType={ButtonStylizedType.Primary}
              text="Okay"
              onClick={handleDecline}
              icon={<BellOff size={28} />}
              additionalStyles={{ 
                width: '100%',
                fontSize: '1.25rem'
              }}
            />
          ) : (
            <>
              <ButtonStylized
                buttonType={ButtonStylizedType.Primary}
                text={isRequesting ? 'Requesting...' : 'Enable Notifications'}
                onClick={handleEnableNotifications}
                disabled={isRequesting}
                icon={<Bell size={28} />}
                additionalStyles={{ 
                  width: '100%',
                  marginBottom: '1rem',
                  fontSize: '1.25rem'
                }}
              />
              
              <ButtonStylized
                buttonType={ButtonStylizedType.Secondary}
                text="Not Now"
                onClick={handleDecline}
                disabled={isRequesting}
                icon={<BellOff size={28} />}
                additionalStyles={{ 
                  width: '100%',
                  fontSize: '1.25rem'
                }}
              />
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};
